/**
 * ApprovalChainView — kompakte Anzeige der mehrstufigen Urlaubsfreigabe.
 *
 * Wird im Urlaubs-Detail/Antrags-Tab eingebunden und zeigt pro Stufe:
 *   - Label (Standortleiter / Niederlassungsleiter / Prokurist …)
 *   - Aktuelle Stufe wird hervorgehoben (Pulsing dot)
 *   - Approver mit Status (✓ approved, ✗ rejected, ⏳ pending)
 */
import React, { useEffect, useState } from 'react';
import { CheckCircle2, XCircle, Clock, Loader2 } from 'lucide-react';
import { vacationsAPI } from '../lib/api';

const STATE_STYLES = {
  approved: { Icon: CheckCircle2, color: 'text-emerald-600', label: 'Genehmigt' },
  rejected: { Icon: XCircle, color: 'text-rose-600', label: 'Abgelehnt' },
  pending: { Icon: Clock, color: 'text-amber-600 animate-pulse', label: 'Wartet auf Entscheidung' },
  waiting: { Icon: Clock, color: 'text-muted-foreground', label: 'Noch nicht an der Reihe' },
};

export const ApprovalChainView = ({ vacationId, compact = false, refreshKey }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    vacationsAPI.getApprovalChain(vacationId)
      .then((res) => { if (active) setData(res.data); })
      .catch(() => { if (active) setData(null); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [vacationId, refreshKey]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-3 w-3 animate-spin" /> Lade Freigabe-Kette …
      </div>
    );
  }
  if (!data || !data.chain || data.chain.length === 0) {
    return <div className="text-xs text-muted-foreground italic">Keine mehrstufige Freigabe konfiguriert.</div>;
  }

  return (
    <div className={`space-y-2 ${compact ? 'text-xs' : 'text-sm'}`} data-testid="vacation-approval-chain">
      {data.chain.map((step) => {
        const style = STATE_STYLES[step.state] || STATE_STYLES.waiting;
        const Icon = style.Icon;
        return (
          <div
            key={step.step}
            className={`flex items-start gap-2 p-2 rounded-md border ${
              step.state === 'pending' ? 'bg-amber-50 dark:bg-amber-950/20 border-amber-200' : 'bg-muted/30 border-border/60'
            }`}
            data-testid={`approval-step-${step.step}`}
          >
            <Icon className={`h-4 w-4 mt-0.5 shrink-0 ${style.color}`} />
            <div className="flex-1 min-w-0">
              <div className="font-medium flex items-center gap-2">
                <span>Stufe {step.step}: {step.label}</span>
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{style.label}</span>
              </div>
              <div className="text-xs text-muted-foreground mt-0.5 truncate">
                {(step.approver_details || []).map((a) => a.name).join(' · ') || '—'}
              </div>
              {step.decisions?.length > 0 && (
                <div className="text-[11px] mt-1 space-y-0.5">
                  {step.decisions.map((d) => (
                    <div key={d.approver_id + d.decided_at} className={d.decision === 'approved' ? 'text-emerald-700' : 'text-rose-700'}>
                      {d.decision === 'approved' ? '✓' : '✗'} {d.approver_name}
                      {d.comment ? ` — „${d.comment}"` : ''}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ApprovalChainView;
